import { addDays, isValid } from 'date-fns';
import sortBy from 'lodash.sortby';
import i18n from 'src/i18n';
import * as Yup from 'yup';

import config from 'config/site.config';
import { createSubUrlFactory } from 'libs/urlFunctions';

const { modules } = config;

class User {
  constructor() {
    this.id = null;
  }

  set name(value) {
    this._name = value;
  }

  get name() {
    return this._name;
  }

  static fromObject(inp) {
    const out = new User();
    if (inp) {
      out.name = inp.name;
      out.id = inp.id;
    }
    return out;
  }
}

export const employmentTypes = {
  SUB_CONTRACTOR: 0,
  SUB_EMPLOYEE: 1,
  EMPLOYEE: 2,
};

export const EMPLOYEE_OR_SUB_EMPLOYEE = -1;

export const getEmploymentTypeDisplayText = (
  employmentType,
  includeSubEmployee = false,
  isFutureEmployment
) => {
  const upcomingLabel = isFutureEmployment
    ? `${i18n.t('Employment.Upcoming')} `
    : '';
  if (employmentType === employmentTypes.SUB_CONTRACTOR) {
    return upcomingLabel + i18n.t('EmploymentType.Subcontractor');
  }

  if (includeSubEmployee && employmentType === employmentTypes.SUB_EMPLOYEE) {
    return upcomingLabel + i18n.t('EmploymentType.SubEmployee');
  }

  return upcomingLabel + i18n.t('EmploymentType.Employee');
};

export const payrollTypes = {
  NO: 0,
  YES: 1,
};

export const getPayrollTypeDisplayText = (payroll) => {
  if (payroll === payrollTypes.YES) {
    return i18n.t('PayrollType.Yes');
  }

  return i18n.t('PayrollType.No');
};

export const accessLevels = {
  USER: 'user',
  SUPER_USER: 'superuser',
  ADMINISTRATOR: 'administrator',
};

export const extendedAccessLevels = {
  ...accessLevels,
  SUBCONTRACTOR_ADMIN: 'subcontractor_admin',
};

export const getAccessLevelDisplayText = (accessLevel) => {
  switch (accessLevel) {
    case accessLevels.SUPER_USER:
      return i18n.t('AccessLevel.Superuser');
    case accessLevels.ADMINISTRATOR:
      return i18n.t('AccessLevel.Administrator');
    default:
      return i18n.t('AccessLevel.User');
  }
};

export const userFetchStatuses = {
  COMPLETE: 'complete',
  LIST: 'list',
};

export const userIsSubContractor = (user) =>
  user.employment?.employmentType === employmentTypes.SUB_CONTRACTOR;

export const userIsFutureEmployee = (user) => {
  if (user.employment) {
    return false;
  }

  if (user.upcomingEmployments?.length) {
    const sorted = sortBy(user.upcomingEmployments, 'startDate');
    return sorted[0].employmentType === employmentTypes.EMPLOYEE;
  }
  return false;
};

export const createUserSubUrlFunction = (userId) =>
  createSubUrlFactory(`${modules.userHandling.detailUrlBase}/${userId}`);

export const getUserDetailUrl = (userId, slug = '') => {
  const getUrl = createUserSubUrlFunction(userId);
  return getUrl(slug);
};

export { User };

const createDateCheck = () =>
  Yup.date()
    .typeError(i18n.t('ValidationError.Required'))
    .required(i18n.t('ValidationError.Required'));

const createEndDateCheck = (startDate) =>
  isValid(startDate)
    ? Yup.date()
        .nullable()
        .typeError(i18n.t('ValidationError.Date_TypeError'))
        .min(
          addDays(startDate ?? new Date(), 1),
          i18n.t('Employment.ShouldBeAfterStartDateError')
        )
    : Yup.date().nullable().typeError(i18n.t('ValidationError.Date_TypeError'));

export const createEmployeeNumberCheck = () =>
  Yup.string()
    .typeError(i18n.t('ValidationError.Required'))
    .when('payroll', {
      is: 1,
      then: (schema) => schema.required(i18n.t('ValidationError.Required')),
    });

export const createPayrollCheck = () =>
  Yup.number()
    .typeError(i18n.t('ValidationError.Required'))
    .required(i18n.t('ValidationError.Required'));

export const createPersonalInfoValidationScheme = (
  isNewUser = false,
  workCardIntegrationEnabled = false,
  prefilled = false
) =>
  Yup.lazy((values) => {
    const shape = {
      firstName: Yup.string()
        .required(i18n.t('ValidationError.Required'))
        .min('2', i18n.t('ValidationError.TooShort')),
      lastName: Yup.string()
        .required(i18n.t('ValidationError.Required'))
        .min('2', i18n.t('ValidationError.TooShort')),
      birthDate: Yup.date()
        .typeError(i18n.t('ValidationError.Required'))
        .required(i18n.t('ValidationError.Required')),
      carRegistrationNumber: Yup.string(),
      closestRelative: Yup.string(),
      email: Yup.string().email(i18n.t('ValidationError.Email')),
      address: Yup.string(),
      description: Yup.string(),
      subContractorAdmin: Yup.boolean().default(false),
      workTitle: Yup.string(),
    };

    if (!prefilled) {
      shape.builderCardNumber = Yup.lazy((value) =>
        value === ''
          ? Yup.string().nullable()
          : Yup.number()
              .typeError(i18n.t('ValidationError.Integer_TypeError'))
              .positive(i18n.t('ValidationError.Integer_TypeError'))
      );
      shape.builderCardExpirationDate = Yup.date().when(
        ['builderCardNumber', 'employmentType'],
        {
          is: (builderCardNumber, employmentType) =>
            !workCardIntegrationEnabled &&
            !!builderCardNumber &&
            employmentType !== employmentTypes.SUB_EMPLOYEE,
          then: (schema) =>
            schema
              .typeError(i18n.t('ValidationError.Required'))
              .required(i18n.t('ValidationError.Required')),
          otherwise: (schema) => schema.nullable(),
        }
      );
    }

    if (isNewUser) {
      shape.phoneNumber = Yup.string()
        .phone(i18n.t('ValidationError.PhoneNumber'))
        .required(i18n.t('ValidationError.Required'));
    } else {
      return Yup.object().shape(shape);
    }

    const isSub = values.employmentType === employmentTypes.SUB_CONTRACTOR;

    if (isSub) {
      shape.startDate = createDateCheck();
      shape.endDate = createEndDateCheck(values.startDate);
    } else {
      shape.department = Yup.string().nullable();

      if (values.employmentType !== employmentTypes.SUB_EMPLOYEE) {
        shape.employeeNumber = createEmployeeNumberCheck();
      } else {
        shape.employeeNumber = Yup.string().nullable();
      }

      shape.startDate = createDateCheck();
      shape.endDate = createEndDateCheck(values.startDate);
      shape.payroll = createPayrollCheck();
    }

    return Yup.object().shape(shape);
  });

export const mapSelectedSubcontractorToEmploymentRequest = (
  company,
  companyName
) => {
  // company name used if no companies found in brreg or ditio db
  const companyRequest = {};

  if (company && company.companyId) {
    companyRequest.companyId = company.companyId;
  } else if (company && company.organizationNumber) {
    companyRequest.organizationNumber = company.organizationNumber;
  } else if (company && company.roaringIntegrationId) {
    companyRequest.roaringIntegrationId = company.roaringIntegrationId;
  } else {
    companyRequest.companyName = companyName;
  }

  return companyRequest;
};

export const disabledReasons = {
  DISABLED_BY_MIGRATION: 'userDisabledByMigration',
  DISABLED_BY_USER: 'userDisabledByUser',
  DISABLED_BY_PHONE_NUMBER_CHANGE: 'userDisabledByPhoneNumberChange',
  DISABLED_BY_EMPLOYMENT: 'userDisabledByEmployment',
  DISABLED_BY_INTEGRATION: 'userDisabledByIntegration',
};

export const showRegistrationDialogParam = 'showRegistrationDialog';
export const showRegistrationCompleteParam = 'showRegistrationComplete';
export const createEmploymentParam = 'createEmployment';
export const endEmploymentParam = 'endEmployment';

const toFirstCapatalize = (word) =>
  `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}`;

export const normalizeName = (name) => {
  if (typeof name === 'undefined' || name === null) {
    return null;
  }

  const trimmedName = name.trim();

  if (trimmedName === '') {
    return trimmedName;
  }

  let result = '';

  const splittedOnSpace = trimmedName.split(' ');

  for (let i = 0; i < splittedOnSpace.length; i += 1) {
    const namePart = splittedOnSpace[i];
    let normalizedPart = '';

    if (namePart.includes('-')) {
      const splittedOnDash = namePart.split('-');
      normalizedPart += splittedOnDash.reduce((acc, part) => {
        if (acc === '') {
          return toFirstCapatalize(part);
        }

        return `${acc}-${toFirstCapatalize(part)}`;
      }, '');
    } else {
      normalizedPart += toFirstCapatalize(namePart);
    }

    if (result === '') {
      result += normalizedPart;
    } else {
      result += ` ${normalizedPart}`;
    }
  }

  return result;
};

export const activeInactiveAll = {
  ACTIVE: 0,
  INACTIVE: 1,
  ALL: 2,
};
